import { ShoppingBag, Menu, X, User, LogOut } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { getCMSContent } from '../utils/api';
import AuthModal from './AuthModal';
import { useStorefrontData } from '../context/StorefrontDataContext';
import logoTypeDark from '../Logo/LogoType-dark.svg';
import logoTypeLight from '../Logo/LogoType-light.svg';

export default function Navbar() {
  const location = useLocation();
  const path = location.pathname;
  const { totalItems, toggleCart } = useCart();
  const { user, isLoggedIn, setShowAuthModal, showAuthModal, logout } = useAuth();
  const { cms } = useStorefrontData();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [siteTitle, setSiteTitle] = useState('SECERA');
  const [scrolled, setScrolled] = useState(false);
  const [footerInfo, setFooterInfo] = useState({
    email: 'care@secera.id',
    phone: '6285750990000',
    copyright: '©Copyright 2026 Secera'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  const [slugs, setSlugs] = useState({
    shop: '/shop',
    about: '/about'
  });

  const transparentPages = ['/', '/shop', '/koleksi', '/about', slugs.shop, slugs.about];
  const isSolidPage = !transparentPages.includes(path) ||
    (path.split('/').length === 3 && path.split('/')[1] !== 'admin') ||
    ['/my-orders', '/profile', '/checkout'].includes(path);

  useEffect(() => {
    const cached = sessionStorage.getItem('secera_page_slugs');
    if (cached) {
      try {
        setSlugs(JSON.parse(cached));
        return;
      } catch (_) {}
    }

    const timeoutId = setTimeout(async () => {
      try {
        // Fetch individual page slugs
        const [shopData, aboutData] = await Promise.all([
          getCMSContent('shop_page'),
          getCMSContent('about_page')
        ]);

        const newSlugs = {
          shop: shopData?.seo?.slug ? `/${shopData.seo.slug}` : '/shop',
          about: aboutData?.seo?.slug ? `/${aboutData.seo.slug}` : '/about'
        };

        setSlugs(newSlugs);
        sessionStorage.setItem('secera_page_slugs', JSON.stringify(newSlugs));
      } catch (error) {
        console.error('Failed to load page slugs:', error);
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (cms?.global?.siteTitle) {
      setSiteTitle(cms.global.siteTitle.split('|')[0].trim());
    }
    if (cms?.footer) {
      setFooterInfo({
        email: cms.footer.email || 'care@secera.id',
        phone: cms.footer.phone || '6285750990000',
        copyright: cms.footer.copyright || '©Copyright 2026 Secera'
      });
    }
  }, [cms]);

  const header = cms?.header;
  const announcement = header?.announcement;
  const isAnnouncementVisible = Boolean(announcement?.enabled && announcement?.text);

  const headerLogo = header?.logo;
  const customLightLogo = headerLogo?.lightUrl;
  const customDarkLogo = headerLogo?.darkUrl;
  const logoHeight = headerLogo?.heightPx || 20;

  const activeLogoSrc = (scrolled || isSolidPage)
    ? (customDarkLogo || logoTypeDark)
    : (customLightLogo || logoTypeLight);

  const isSafeUrl = (url?: string): boolean => {
    if (!url) return false;
    const trimmed = url.trim().toLowerCase();
    if (trimmed.startsWith('javascript:') || trimmed.startsWith('data:') || trimmed.startsWith('vbscript:')) {
      return false;
    }
    return trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('http://') || trimmed.startsWith('https://');
  };

  const dynamicMenuItems = header?.menuItems;
  const links = (Array.isArray(dynamicMenuItems) && dynamicMenuItems.length > 0)
    ? dynamicMenuItems
        .filter((item) => item?.url && isSafeUrl(item.url))
        .map((item) => ({
          to: item.url,
          label: item.label,
          isExternal: Boolean(item.isExternal || item.url?.startsWith('http'))
        }))
    : [
        { to: '/', label: 'Beranda', isExternal: false },
        { to: slugs.shop, label: 'Koleksi', isExternal: false },
        { to: slugs.about, label: 'Tentang', isExternal: false }
      ];

  const hasSafeAnnouncementLink = announcement?.link && isSafeUrl(announcement.link);

  return (
    <>
      <motion.div
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50 flex flex-col"
      >
        {/* Sticky Top Announcement Bar */}
        {isAnnouncementVisible && (
          <div className="bg-[#722F38] text-white text-[11px] md:text-xs font-medium tracking-wide py-1.5 px-4 text-center border-b border-[#5a252d] flex items-center justify-center gap-2 shrink-0">
            {hasSafeAnnouncementLink ? (
              announcement!.link.startsWith('http') ? (
                <a
                  href={announcement!.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline flex items-center gap-1.5 transition-opacity hover:opacity-90"
                >
                  <span>{announcement!.text}</span>
                  <span className="opacity-70 text-[10px]">↗</span>
                </a>
              ) : (
                <Link
                  to={announcement!.link}
                  className="hover:underline flex items-center gap-1.5 transition-opacity hover:opacity-90"
                >
                  <span>{announcement!.text}</span>
                  <span className="opacity-70 text-[10px]">→</span>
                </Link>
              )
            ) : (
              <span>{announcement?.text}</span>
            )}
          </div>
        )}

        <motion.nav
          animate={{
            backgroundColor: scrolled || isSolidPage ? '#fff' : 'rgba(0, 0, 0, 0)',
            borderColor: scrolled || isSolidPage ? 'rgba(38, 31, 31, 0.16)' : 'rgba(0, 0, 0, 0)',
            paddingTop: scrolled || isSolidPage ? '12px' : '16px',
            paddingBottom: scrolled || isSolidPage ? '12px' : '16px'
          }}
          className="md:px-[clamp(1.5rem,5vw,6rem)] pl-4 pr-2 flex items-center justify-between border-b relative"
        >
          <Link to="/" className="relative z-10 block">
            <img
              src={activeLogoSrc}
              alt={siteTitle}
              className="w-auto object-contain transition-all"
              style={{ height: `${logoHeight}px` }}
              decoding="async"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10 relative z-10">
            {links.map((link) => (
              link.isExternal ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-label transition-all ${
                    scrolled || isSolidPage ? 'text-[#5A252D]/55 hover:text-[#5A252D]' : 'text-[#F9F9F9]/70 hover:text-[#F9F9F9]'
                  }`}
                >
                  <span>{link.label}</span>
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-label transition-all ${path === link.to
                    ? (scrolled || isSolidPage ? 'text-[#5A252D]' : 'text-[#F9F9F9]')
                    : (scrolled || isSolidPage ? 'text-[#5A252D]/55 hover:text-[#5A252D]' : 'text-[#F9F9F9]/70 hover:text-[#F9F9F9]')
                    }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          <div className="flex items-center gap-6 relative z-10">

            <div className="flex items-center gap-3">
              {/* Cart */}
              <button
                onClick={toggleCart}
                className={`relative ${scrolled || isSolidPage ? 'text-[#5A252D] border-[#5A252D]/20 hover:bg-[#5A252D]/5' : 'text-[#F9F9F9] border-[#F9F9F9]/35 hover:bg-[#F9F9F9]/10'} transition-colors p-2.5 border`}
                aria-label="Keranjang belanja"
              >
                <ShoppingBag className="w-4 h-4" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 bg-[#5A252D] text-white text-[8px] font-black flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>

              {/* User / Logout */}
              {isLoggedIn ? (
                <>
                  <Link
                    to="/akun"
                    className={`${scrolled || isSolidPage ? 'text-[#5A252D] border-[#5A252D]/20 hover:bg-[#5A252D]/5' : 'text-[#F9F9F9] border-[#F9F9F9]/35 hover:bg-[#F9F9F9]/10'} transition-colors p-2.5 border`}
                    title="Akun"
                    aria-label="Akun"
                  >
                    <User className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={logout}
                    className={`hidden md:flex items-center gap-2 text-label px-3 py-2 transition-all ${scrolled || isSolidPage ? 'text-[#5A252D]/60 hover:text-[#5A252D]' : 'text-[#F9F9F9]/60 hover:text-[#F9F9F9]'
                      }`}
                    title="Logout"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Keluar</span>
                  </button>
                </>
              ) : (
                <Link
                  to="/akun"
                  className={`${scrolled || isSolidPage ? 'text-[#5A252D] border-[#5A252D]/20 hover:bg-[#5A252D]/5' : 'text-[#F9F9F9] border-[#F9F9F9]/35 hover:bg-[#F9F9F9]/10'} transition-colors p-2.5 border`}
                  title="Akun"
                  aria-label="Akun"
                >
                  <User className="w-4 h-4" />
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`md:hidden ${scrolled || isSolidPage ? 'text-[#5A252D]' : 'text-[#F9F9F9]'} hover:opacity-70 transition-opacity p-2`}
                aria-label={mobileOpen ? "Tutup menu" : "Buka menu"}
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </motion.nav>
      </motion.div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-0 z-[60] bg-[#F4F4F4] md:hidden"
          >
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#261f1f]/12">
                <span className="text-label text-[#5A252D]/70">Menu</span>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="text-[#5A252D] hover:opacity-70 transition-opacity p-2 -mr-2"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="flex flex-col gap-4">
                  {links.map((link) => (
                    link.isExternal ? (
                      <a
                        key={link.to}
                        href={link.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileOpen(false)}
                        className={`text-label py-2 flex items-center justify-between ${
                          path === link.to ? 'text-[#722F38]' : 'text-[#3A3A3A]/70 hover:text-[#722F38]'
                        }`}
                      >
                        <span>{link.label}</span>
                        <span className="text-[10px] text-muted">↗</span>
                      </a>
                    ) : (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setMobileOpen(false)}
                        className={`text-label py-2 ${path === link.to
                          ? 'text-[#722F38]'
                          : 'text-[#3A3A3A]/70 hover:text-[#722F38]'
                          }`}
                      >
                        {link.label}
                      </Link>
                    )
                  ))}
                  {isLoggedIn && (
                    <button
                      onClick={() => {
                        logout();
                        setMobileOpen(false);
                      }}
                      className="text-label text-red-500 hover:text-red-600 py-2 flex items-center gap-2 border-t border-gray-100 pt-4"
                    >
                      <LogOut className="w-4 h-4" />
                      Keluar
                    </button>
                  )}
                </div>
              </div>

              {/* Mobile Sidebar Footer */}
              <div className="px-6 py-8 border-t border-[#261f1f]/12 bg-white/50 space-y-4">
                <div className="space-y-2">
                  <p className="text-label text-[#5A252D]/40">Hubungi Kami</p>
                  <div className="space-y-1">
                    <a href={`mailto:${footerInfo.email}`} className="block text-sm text-[#5A252D] hover:text-[#722F38] transition-colors">{footerInfo.email}</a>
                    <a href={`https://wa.me/${footerInfo.phone}`} target="_blank" rel="noopener noreferrer" className="block text-sm text-[#5A252D] hover:text-[#722F38] transition-colors">+{footerInfo.phone}</a>
                  </div>
                </div>
                <p className="text-label text-[#5A252D]/25 pt-2">{footerInfo.copyright}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
}
